/**
 * [가가 텍스트 스타일]
 * 역할: 선택 구간에만 적용되는 인라인 글자 크기/굵기/모노 마크
 * 작성/수정일: 2026-07-29
 */
import { Mark, mergeAttributes } from '@tiptap/core';

export const FONT_VARIANT_STYLES = {
  title: { fontSize: '2rem', fontWeight: '700', lineHeight: '1.25' },
  heading: { fontSize: '1.5rem', fontWeight: '700', lineHeight: '1.3' },
  subheading: { fontSize: '1.25rem', fontWeight: '600', lineHeight: '1.35' },
  // heading 블록 안에서도 선택 구간만 본문 크기로 되돌리기 위함
  body: { fontSize: '1rem', fontWeight: '400', lineHeight: '1.45' },
  mono: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: '0.9em',
    fontWeight: '400',
  },
};

function styleFromVariant(variant) {
  const spec = FONT_VARIANT_STYLES[variant];
  if (!spec) return null;
  return Object.entries(spec)
    .map(([key, value]) => {
      const cssKey = key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
      return `${cssKey}: ${value}`;
    })
    .join('; ');
}

function variantFromElement(el) {
  const marked = el.getAttribute('data-font-variant');
  if (marked && FONT_VARIANT_STYLES[marked]) return marked;

  const fontSize = (el.style?.fontSize || '').trim();
  const fontFamily = (el.style?.fontFamily || '').toLowerCase();
  if (fontFamily.includes('monospace') || fontFamily.includes('menlo') || fontFamily.includes('consolas')) {
    return 'mono';
  }
  if (fontSize === '2rem' || fontSize === '32px') return 'title';
  if (fontSize === '1.5rem' || fontSize === '24px') return 'heading';
  if (fontSize === '1.25rem' || fontSize === '20px') return 'subheading';
  return null;
}

export const FontVariant = Mark.create({
  name: 'fontVariant',

  // [강조 색] TextStyle보다 바깥에 렌더 → 큰 font-size가 배경 span에 상속 — 작성/수정일: 2026-08-12
  priority: 110,

  excludes: '',

  addAttributes() {
    return {
      variant: {
        default: null,
        parseHTML: (element) => variantFromElement(element),
        renderHTML: (attributes) => {
          const variant = attributes.variant;
          if (!variant || !FONT_VARIANT_STYLES[variant]) return {};
          const style = styleFromVariant(variant);
          return {
            'data-font-variant': variant,
            class: `g5-gurumi-font-${variant}`,
            style,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      { tag: 'span[data-font-variant]' },
      {
        tag: 'span',
        getAttrs: (el) => {
          const variant = variantFromElement(el);
          return variant ? { variant } : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      // [가가 텍스트 스타일] 역할: 선택 텍스트에 variant 적용 — 작성/수정일: 2026-07-29
      setFontVariant:
        (variant) =>
        ({ commands }) => {
          if (!variant || !FONT_VARIANT_STYLES[variant]) return false;
          return commands.setMark(this.name, { variant });
        },
      // [가가 텍스트 스타일] 역할: 선택 텍스트 스타일 제거(본문) — 작성/수정일: 2026-07-29
      unsetFontVariant:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});
